import styled from '@emotion/styled';
import {
  Box,
  Button,
  ButtonBase,
  InputAdornment,
  Popover,
  TextField
} from '@material-ui/core';
import React, { useMemo, useState } from 'react';
import { useModel } from '../../hooks/useModel';
import { useUnsafeTranslation } from '../../i18next';
import { FlexContainer } from '../../layout/Flex';
import { useTheme } from '../../themes';
import { ButtonLoader } from '../ButtonWithPromise';

type Props = {
  value: string;
  onChange: (nextValue: string) => void;
  size?: number;
  colorSet?: string[];
  noMargin?: boolean;
  disabled?: boolean;
};

const DEFAULT_COLOR_SET_HUES = [
  'blue',
  'green',
  'yellow',
  'red',
  'purple'
] as const;
const DEFAULT_COLOR_SET_SHADES = ['w300', 'w500', 'w700'] as const;

/** The picker's default swatches: five hues at three strengths, lightest row first. */
export const getDefaultColorSet = (
  colors: ReturnType<typeof useTheme>['custom']['colors']
): string[] =>
  DEFAULT_COLOR_SET_SHADES.flatMap((shade) =>
    DEFAULT_COLOR_SET_HUES.map((hue) => colors[hue][shade])
  );

const Swatches = styled('div')`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  grid-gap: ${(p) => p.theme.spacing(1.5)}px;
`;

export const Swatch = styled(ButtonBase)<{
  color: string;
  size: number;
}>`
  && {
    display: inline-block;
    width: ${(p) => p.size}px;
    height: ${(p) => p.size}px;
    border-radius: 4px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    background-color: ${(p) => p.color};
    color: ${(p) => p.color};
    cursor: ${(p) => (p.disabled ? 'default' : 'pointer')};
  }
`;

export const ColorPicker = ({
  value = '',
  onChange,
  size = 24,
  colorSet,
  disabled = false
}: Props) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );
  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const colors = useMemo(
    () => colorSet || getDefaultColorSet(theme.custom.colors),
    [colorSet, theme]
  );

  const open = Boolean(anchorEl);

  return (
    <FlexContainer>
      <Swatch
        type="button"
        size={size}
        color={value}
        disabled={disabled}
        onClick={handleClick}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        elevation={8}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
      >
        <Box p={2} style={{ maxWidth: '3350px' }}>
          <Swatches>
            {colors.map((c) => (
              <Swatch
                type="button"
                size={theme.spacing(3)}
                key={c}
                color={c}
                onClick={() => {
                  onChange(c);
                  handleClose();
                }}
              />
            ))}
          </Swatches>
        </Box>
      </Popover>
    </FlexContainer>
  );
};

export const TextFieldColor = ({
  value,
  onChange,
  fullWidth
}: {
  value: string;
  onChange: (nextValue: string) => void;
  fullWidth?: boolean;
}) => {
  return (
    <TextField
      value={value}
      onChange={(ev) => onChange(ev.target.value)}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Swatch size={24} color={value} />
          </InputAdornment>
        )
      }}
      fullWidth={fullWidth}
    />
  );
};

export const TextFieldColorForm = ({
  value,
  onSave,
  fullWidth
}: {
  value: string;
  onSave: (nextValue: string) => Promise<void>;
  fullWidth?: boolean;
}) => {
  const [model, setModel] = useModel(value);
  const [loading, setLoading] = useState(false);
  const { t } = useUnsafeTranslation();
  return (
    <form
      onSubmit={async (ev) => {
        ev.preventDefault();
        setLoading(true);
        await onSave(model);
        setLoading(false);
      }}
    >
      <FlexContainer fullWidth={fullWidth}>
        <TextFieldColor
          value={model}
          onChange={(nextColor) => setModel(nextColor)}
        />
        {model !== value && (
          <>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <ButtonLoader /> t{'Saving...'}
                </>
              ) : (
                t('Save')
              )}
            </Button>
            <Button onClick={() => setModel(value)}>{t('Reset')}</Button>
          </>
        )}
      </FlexContainer>
    </form>
  );
};
